import {
  CalendarIcon,
  DateView,
  DesktopDatePicker,
  LocalizationProvider,
  MobileDatePicker,
  PickersLayout,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { theme } from '../../theme/theme'
import styled, { css } from 'styled-components'
import { isMobile } from '../../utils/devices'
import InputAdornment from '@mui/material/InputAdornment'
import { sv, enGB, nb } from 'date-fns/locale'
import { useSelectUserLanguage } from '../../state/selectors'
import { SupportedLanguage } from '@estercare/ester-shared'

type DateProps = {
  disabled?: boolean
  answer: Date | null
  onChange: (value: Date | null) => void
  granularity: DateView[]
  disableFuture?: boolean
}

export const DateInput = ({ answer, onChange, granularity, disableFuture }: DateProps) => {
  const width = window.innerWidth
  const userLanguage = useSelectUserLanguage()

  const getFormat = () =>
    granularity.includes('day') ? 'd MMMM yyyy' : granularity.includes('month') ? 'MMMM yyyy' : 'yyyy'

  const commonProps = {
    value: answer,
    onChange: (newValue: Date | null) => onChange(newValue),
    views: granularity,
    disableFuture,
  }

  const getAdapterLocale = () => {
    switch (userLanguage) {
      case SupportedLanguage.ENGLISH:
        return enGB
      case SupportedLanguage.NORWEGIAN:
        return nb
      case SupportedLanguage.SWEDISH:
      default:
        return sv
    }
  }

  const adapterLocale = getAdapterLocale()

  return (
    <DateContainer>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
        {isMobile(width) ? (
          <StyledMobileDatePicker
            {...commonProps}
            slots={{
              layout: StyledMobilePickersLayout,
            }}
            slotProps={{
              textField: {
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarIcon style={{ color: theme.color.plum }} />
                    </InputAdornment>
                  ),
                },
              },
              toolbar: {
                toolbarFormat: getFormat(),
              },
            }}
          />
        ) : (
          <StyledDesktopDatePicker
            {...commonProps}
            slots={{
              layout: StyledDesktopPickersLayout,
            }}
            slotProps={{
              popper: {
                sx: { '& .MuiPaper-root': { boxShadow: 'none' } },
              },
            }}
          />
        )}
      </LocalizationProvider>
    </DateContainer>
  )
}

const DateContainer = styled.div`
  display: flex;
`

const datePickerStyles = css`
  .MuiOutlinedInput-root {
    border-radius: 6px;
    background-color: ${theme.color.white};

    &:hover .MuiOutlinedInput-notchedOutline,
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.color.plumMid};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 2px;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${theme.color.beigeDark};
  }
`

const StyledDesktopDatePicker = styled(DesktopDatePicker)`
  width: 324px;
  ${datePickerStyles}
`

const StyledMobileDatePicker = styled(MobileDatePicker)`
  width: 100%;
  ${datePickerStyles}
`

const commonPickersLayoutStyles = css`
  .MuiPickersLayout-contentWrapper {
    background-color: ${theme.color.white};
  }

  .MuiPickersCalendarHeader-root,
  .MuiPickersCalendarHeader-label,
  .MuiPickersArrowSwitcher-root button {
    color: ${theme.color.plum};
  }

  && {
    .MuiPickersDay-root.Mui-selected,
    .MuiPickersMonth-root .Mui-selected,
    .MuiPickersYear-root .Mui-selected {
      background-color: ${theme.color.plum};
      &:hover {
        background-color: ${theme.color.plumMid};
      }
    }
  }

  .MuiPickersDay-root.Mui-selected.MuiPickersDay-today {
    border: 1px solid ${theme.color.plum};
  }
`

const StyledMobilePickersLayout = styled(PickersLayout)`
  .MuiDialogActions-root,
  .MuiPickersToolbar-root {
    background-color: ${theme.color.white};
  }

  .MuiDialogActions-root button {
    color: ${theme.color.plumMid};
  }
  ${commonPickersLayoutStyles}
`

const StyledDesktopPickersLayout = styled(PickersLayout)`
  .MuiPickersLayout-contentWrapper {
    border-radius: 6px;
    border: 2px solid ${theme.color.beigeDark};
  }
  ${commonPickersLayoutStyles}
`
